/**
 * InlineTextEditor hook — enables Ctrl+click inline editing of elements within
 * raw HTML sections.
 *
 * Detects element type and shows the appropriate editor:
 * - Text elements → native contenteditable with floating format toolbar (B/I/U/color)
 * - Images/icons → floating overlay with URL / SVG selection / file upload / alt text
 * - Buttons/Links → floating overlay with text, URL, new-tab toggle
 *
 * Place on a wrapper element around the raw HTML preview container.
 * Listens for "start-inline-edit" push events from the server.
 */

const BUTTON_TAGS = new Set(["a", "button"]);
const IMAGE_TAGS = new Set(["img"]);
const ICON_TAGS = new Set(["svg"]);
const UPLOAD_PATH = "/admin/inline-upload/image";

export const InlineTextEditor = {
  mounted() {
    this.currentElement = null;
    this.originalHTML = null;
    this.toolbar = null;
    this.popup = null;

    this.handleEvent("start-inline-edit", ({ post_id, element_id }) => {
      this.startEdit(post_id, element_id);
    });

    // An assistant advisory names an element by its data-assistant-id. Every
    // post's editor receives this event; only the one holding that element
    // reacts, so no post-id comparison is needed.
    this.handleEvent("reveal-page-element", ({ element_id }) => {
      this.revealElement(element_id);
    });
  },

  revealElement(elementId) {
    const el = this.el.querySelector(`[data-assistant-id="${elementId}"]`);
    if (!el) return;

    el.scrollIntoView({ behavior: "smooth", block: "center" });

    el.classList.remove("ai-advisory-highlight");
    // Force a reflow so re-clicking the same anchor replays the highlight
    // rather than doing nothing because the class never left.
    void el.offsetWidth;
    el.classList.add("ai-advisory-highlight");

    clearTimeout(this._revealTimeout);
    this._revealTimeout = setTimeout(() => {
      el.classList.remove("ai-advisory-highlight");
    }, 2400);
  },

  destroyed() {
    clearTimeout(this._revealTimeout);
    this.cleanup();
  },

  startEdit(postId, elementId) {
    this.cleanup();

    const el = this.el.querySelector(`[data-assistant-id="${elementId}"]`);
    if (!el) return;

    const tagName = el.tagName.toLowerCase();
    const innerLink = el.querySelector("a, button");
    const innerImg = el.querySelector("img");
    const innerSvg = el.querySelector("svg");

    if (IMAGE_TAGS.has(tagName)) {
      this.startImageEdit(postId, elementId, el);
    } else if (ICON_TAGS.has(tagName)) {
      this.startImageEdit(postId, elementId, el, el);
    } else if (BUTTON_TAGS.has(tagName)) {
      this.startButtonEdit(postId, elementId, el, el);
    } else if (innerImg) {
      this.startImageEdit(postId, elementId, el, innerImg);
    } else if (innerSvg) {
      this.startImageEdit(postId, elementId, el, innerSvg);
    } else if (innerLink) {
      this.startButtonEdit(postId, elementId, el, innerLink);
    } else {
      this.startTextEdit(postId, elementId, el);
    }
  },

  // ── Text editing (native contenteditable + floating format toolbar) ──────

  startTextEdit(postId, elementId, el) {
    this.currentElement = el;
    this.originalHTML = el.innerHTML;
    el.classList.add("inline-editing");
    el.contentEditable = "true";
    el.focus();

    // Place cursor at end
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);

    this.showTextToolbar(postId, elementId, el);
  },

  showTextToolbar(postId, elementId, targetEl) {
    if (this.toolbar) this.toolbar.remove();

    const toolbar = document.createElement("div");
    toolbar.className = "inline-edit-toolbar";

    // Format buttons — use mousedown so we don't lose selection
    const boldBtn = this.createFormatBtn("<strong>B</strong>", "Bold", () =>
      document.execCommand("bold", false, null),
    );
    const italicBtn = this.createFormatBtn(
      "<em style='font-style:italic'>I</em>",
      "Italic",
      () => document.execCommand("italic", false, null),
    );
    const underlineBtn = this.createFormatBtn("<u>U</u>", "Underline", () =>
      document.execCommand("underline", false, null),
    );

    // Color picker (hidden input triggered by label click)
    const colorLabel = document.createElement("label");
    colorLabel.title = "Text Color";
    colorLabel.className =
      "inline-flex items-center justify-center w-7 h-7 cursor-pointer border border-slate-200 rounded hover:bg-slate-50 text-sm font-bold text-slate-700 select-none";
    colorLabel.style.lineHeight = "1";
    colorLabel.innerHTML = `<span style="border-bottom:3px solid #6366f1;padding-bottom:1px;">A</span>`;
    const colorInput = document.createElement("input");
    colorInput.type = "color";
    colorInput.value = "#000000";
    colorInput.style.cssText =
      "position:absolute;opacity:0;width:1px;height:1px;pointer-events:none;";
    colorInput.addEventListener("input", () => {
      document.execCommand("foreColor", false, colorInput.value);
    });
    colorLabel.appendChild(colorInput);
    colorLabel.addEventListener("mousedown", (e) => {
      e.preventDefault(); // Keep selection in contenteditable
      colorInput.click();
    });

    // Divider
    const sep = document.createElement("div");
    sep.style.cssText =
      "width:1px;background:#e2e8f0;margin:2px 2px;align-self:stretch;";

    const saveBtn = this.createButton("Save", "indigo", () => {
      this.pushEvent("save-inline-edit", {
        post_id: postId,
        element_id: elementId,
        new_html: targetEl.innerHTML,
      });
      this.cleanup();
    });
    const deleteBtn = this.createDeleteButton(postId, elementId);
    const cancelBtn = this.createButton("Cancel", "slate", () => this.cancel());

    [
      boldBtn,
      italicBtn,
      underlineBtn,
      colorLabel,
      sep,
      saveBtn,
      deleteBtn,
      cancelBtn,
    ].forEach((b) => toolbar.appendChild(b));

    this.positionFloatingBar(toolbar, targetEl);
    this.toolbar = toolbar;
  },

  createFormatBtn(html, title, onMousedown) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.title = title;
    btn.className =
      "inline-flex items-center justify-center w-7 h-7 text-sm text-slate-700 bg-white border border-slate-200 rounded hover:bg-slate-100 transition-colors";
    btn.innerHTML = html;
    btn.addEventListener("mousedown", (e) => {
      e.preventDefault(); // Prevents contenteditable losing focus
      onMousedown();
    });
    return btn;
  },

  // ── Image editing (floating popup) ───────────────────────────────────────

  startImageEdit(postId, elementId, el, innerImg) {
    this.currentElement = el;
    el.classList.add("inline-editing");

    const mediaEl = innerImg || el;
    const isSvg = mediaEl.tagName.toLowerCase() === "svg";
    const currentSrc =
      mediaEl.getAttribute("src") || mediaEl.dataset.imageSrc || "";
    const currentAlt =
      mediaEl.getAttribute("alt") || mediaEl.getAttribute("aria-label") || "";
    const availableSvgs = this.getAvailableSvgs();
    const svgOptions = availableSvgs
      .map(
        ({ label }, index) =>
          `<option value="${index}">${this.escapeHtml(label)}</option>`,
      )
      .join("");
    let selectedSvgIcon = null;

    const popup = this.createFloatingPopup();
    popup.innerHTML = `
      <div class="iep-title">${isSvg ? "Edit Icon" : "Edit Image"}</div>
      <img id="iep-img-preview"
           src="${this.escapeAttr(currentSrc)}"
           alt="Preview"
           style="max-height:80px;max-width:100%;border-radius:0.375rem;border:1px solid #e2e8f0;margin-bottom:0.75rem;display:${currentSrc ? "block" : "none"};" />
      <div id="iep-svg-preview"
           style="max-height:80px;max-width:100%;border-radius:0.375rem;border:1px solid #e2e8f0;margin-bottom:0.75rem;padding:0.5rem;display:none;align-items:center;justify-content:center;color:#334155;"></div>
      <label class="iep-label">Image URL</label>
      <input type="text" id="iep-img-src" class="iep-input" value="${this.escapeAttr(currentSrc)}" placeholder="https://..." />
      ${
        availableSvgs.length
          ? `<label class="iep-label iep-or">— or choose an SVG —</label>
             <select id="iep-img-svg" class="iep-input">
               <option value="">Select an SVG</option>
               ${svgOptions}
             </select>`
          : ""
      }
      <label class="iep-label iep-or">— or upload a file —</label>
      <input type="file" id="iep-img-file" accept="image/*" class="iep-file" />
      <div id="iep-upload-status" class="iep-status" style="display:none;"></div>
      <label class="iep-label">Alt Text</label>
      <input type="text" id="iep-img-alt" class="iep-input" value="${this.escapeAttr(currentAlt)}" placeholder="Describe the image" />
      <div class="iep-actions"></div>
    `;

    const actionsDiv = popup.querySelector(".iep-actions");
    const saveBtn = this.createButton("Save", "indigo", () => {
      const src = popup.querySelector("#iep-img-src").value;
      const alt = popup.querySelector("#iep-img-alt").value;
      this.pushEvent("save-inline-image", {
        post_id: postId,
        element_id: elementId,
        src,
        alt,
        svg_icon: selectedSvgIcon,
      });
      this.cleanup();
    });
    const deleteBtn = this.createDeleteButton(postId, elementId);
    const cancelBtn = this.createButton("Cancel", "slate", () =>
      this.cleanup(),
    );
    actionsDiv.appendChild(saveBtn);
    actionsDiv.appendChild(deleteBtn);
    actionsDiv.appendChild(cancelBtn);

    // Live URL preview
    const srcInput = popup.querySelector("#iep-img-src");
    const preview = popup.querySelector("#iep-img-preview");
    const svgPreview = popup.querySelector("#iep-svg-preview");
    const altInput = popup.querySelector("#iep-img-alt");
    srcInput.addEventListener("input", () => {
      selectedSvgIcon = null;
      if (svgSelect) svgSelect.value = "";
      svgPreview.innerHTML = "";
      svgPreview.style.display = "none";
      preview.src = srcInput.value;
      preview.style.display = srcInput.value ? "block" : "none";
    });

    const svgSelect = popup.querySelector("#iep-img-svg");
    if (svgSelect) {
      svgSelect.addEventListener("change", () => {
        if (!svgSelect.value) return;
        const selected = availableSvgs[Number(svgSelect.value)];
        if (!selected) return;
        selectedSvgIcon = selected.icon;
        srcInput.value = "";
        preview.removeAttribute("src");
        preview.style.display = "none";
        svgPreview.innerHTML = this.iconPreviewSvg(selected.icon);
        svgPreview.style.display = "flex";
        if (!altInput.value) {
          altInput.value = selected.label;
        }
      });
    }

    // File upload via fetch
    const fileInput = popup.querySelector("#iep-img-file");
    const statusEl = popup.querySelector("#iep-upload-status");
    fileInput.addEventListener("change", async () => {
      const file = fileInput.files[0];
      if (!file) return;
      statusEl.style.display = "block";
      statusEl.textContent = "Uploading…";
      statusEl.style.color = "#6366f1";
      saveBtn.disabled = true;
      try {
        const formData = new FormData();
        formData.append("image", file);
        const csrf =
          document
            .querySelector("meta[name='csrf-token']")
            ?.getAttribute("content") || "";
        const res = await fetch(UPLOAD_PATH, {
          method: "POST",
          headers: { "x-csrf-token": csrf },
          body: formData,
        });
        if (!res.ok) throw new Error(`${res.status}`);
        const data = await res.json();
        selectedSvgIcon = null;
        if (svgSelect) svgSelect.value = "";
        svgPreview.innerHTML = "";
        svgPreview.style.display = "none";
        srcInput.value = data.url;
        preview.src = data.url;
        preview.style.display = "block";
        if (!altInput.value) altInput.value = file.name.replace(/\.[^.]+$/, "");
        statusEl.textContent = "Uploaded!";
        statusEl.style.color = "#16a34a";
        setTimeout(() => (statusEl.style.display = "none"), 2000);
      } catch (err) {
        statusEl.textContent = `Upload failed: ${err.message}`;
        statusEl.style.color = "#dc2626";
      } finally {
        saveBtn.disabled = false;
      }
    });

    this.positionPopupNear(popup, el);
    this.popup = popup;
    srcInput.focus();
  },

  // ── Button / Link editing (floating popup) ────────────────────────────────

  startButtonEdit(postId, elementId, wrapperEl, targetEl) {
    this.currentElement = wrapperEl;
    wrapperEl.classList.add("inline-editing");

    const currentText = targetEl.textContent.trim();
    const currentHref = targetEl.getAttribute("href") || "";
    const currentNewTab = targetEl.getAttribute("target") === "_blank";

    const popup = this.createFloatingPopup();
    popup.innerHTML = `
      <div class="iep-title">Edit Button / Link</div>
      <label class="iep-label">Button Text</label>
      <input type="text" id="iep-btn-text" class="iep-input" value="${this.escapeAttr(currentText)}" placeholder="Button label" />
      <label class="iep-label">Link URL</label>
      <input type="text" id="iep-btn-href" class="iep-input" value="${this.escapeAttr(currentHref)}" placeholder="https://..." />
      <label class="iep-checkbox-label">
        <input type="checkbox" id="iep-btn-newtab" ${currentNewTab ? "checked" : ""} />
        Open in new tab
      </label>
      <div class="iep-actions"></div>
    `;

    const actionsDiv = popup.querySelector(".iep-actions");
    const saveBtn = this.createButton("Save", "indigo", () => {
      const text = popup.querySelector("#iep-btn-text").value;
      const href = popup.querySelector("#iep-btn-href").value;
      const newTab = popup.querySelector("#iep-btn-newtab").checked;
      this.pushEvent("save-inline-button", {
        post_id: postId,
        element_id: elementId,
        text,
        href,
        new_tab: newTab,
      });
      this.cleanup();
    });
    const deleteBtn = this.createDeleteButton(postId, elementId);
    const cancelBtn = this.createButton("Cancel", "slate", () =>
      this.cleanup(),
    );
    actionsDiv.appendChild(saveBtn);
    actionsDiv.appendChild(deleteBtn);
    actionsDiv.appendChild(cancelBtn);

    this.positionPopupNear(popup, wrapperEl);
    this.popup = popup;
    popup.querySelector("#iep-btn-text").focus();
    popup.querySelector("#iep-btn-text").select();
  },

  // ── Positioning helpers ───────────────────────────────────────────────────

  createFloatingPopup() {
    const popup = document.createElement("div");
    popup.className = "inline-edit-popup";
    popup.style.position = "fixed";
    popup.style.zIndex = "1000";
    document.body.appendChild(popup);
    return popup;
  },

  positionFloatingBar(toolbar, targetEl) {
    const rect = targetEl.getBoundingClientRect();
    toolbar.style.position = "fixed";
    toolbar.style.top = Math.max(4, rect.top - 44) + "px";
    toolbar.style.left = rect.left + "px";
    toolbar.style.zIndex = "1000";
    toolbar.style.background = "white";
    toolbar.style.padding = "4px 6px";
    toolbar.style.borderRadius = "6px";
    toolbar.style.border = "1px solid #e2e8f0";
    toolbar.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)";
    document.body.appendChild(toolbar);
  },

  positionPopupNear(popup, targetEl) {
    const rect = targetEl.getBoundingClientRect();
    const viewH = window.innerHeight;
    const spaceBelow = viewH - rect.bottom;
    const top =
      spaceBelow > 280 ? rect.bottom + 8 : Math.max(4, rect.top - 320);
    popup.style.top = top + "px";
    popup.style.left =
      Math.max(4, Math.min(rect.left, window.innerWidth - 340)) + "px";
  },

  // ── Shared helpers ────────────────────────────────────────────────────────

  createButton(label, color, onClick) {
    const btn = document.createElement("button");
    btn.type = "button";
    if (color === "indigo") {
      btn.className =
        "inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 transition-colors";
    } else if (color === "red") {
      btn.className =
        "inline-flex items-center px-3 py-1.5 text-xs font-medium text-red-700 bg-white border border-red-300 rounded hover:bg-red-50 transition-colors";
    } else {
      btn.className =
        "inline-flex items-center px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded hover:bg-slate-50 transition-colors";
    }
    const iconPath =
      label === "Save" ? "M5 13l4 4L19 7" : "M6 18L18 6M6 6l12 12";
    btn.innerHTML = `<svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="${iconPath}"/></svg>${label}`;
    btn.addEventListener("click", onClick);
    return btn;
  },

  createDeleteButton(postId, elementId) {
    return this.createButton("Delete", "red", () => {
      const confirmed = window.confirm(
        "Delete this element and everything inside it? This cannot be undone.",
      );
      if (!confirmed) return;

      this.pushEvent("delete-inline-element", {
        post_id: postId,
        element_id: elementId,
      });
      this.cleanup();
    });
  },

  escapeAttr(str) {
    return String(str || "")
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  },

  escapeHtml(str) {
    return this.escapeAttr(str).replace(/'/g, "&#39;");
  },

  getAvailableSvgs() {
    try {
      return JSON.parse(this.el.dataset.availableSvgs || "[]");
    } catch (_err) {
      return [];
    }
  },

  iconPreviewSvg(icon) {
    const label = this.escapeAttr(icon);
    return `<div style="width:64px;height:64px;display:flex;align-items:center;justify-content:center;border-radius:0.375rem;background:#f8fafc;color:#334155;font-size:0.7rem;text-align:center;line-height:1.1;">${label}</div>`;
  },

  cancel() {
    if (this.currentElement && this.originalHTML !== null) {
      this.currentElement.innerHTML = this.originalHTML;
    }
    this.cleanup();
  },

  cleanup() {
    if (this.currentElement) {
      this.currentElement.classList.remove("inline-editing");
      this.currentElement.removeAttribute("contenteditable");
      this.currentElement = null;
    }
    if (this.toolbar) {
      this.toolbar.remove();
      this.toolbar = null;
    }
    if (this.popup) {
      this.popup.remove();
      this.popup = null;
    }
    this.originalHTML = null;
  },
};
